import { useMemo } from "react";
import { useLocations } from "@stockline/web";
import type { Location as OfflineLocation } from "@stockline/shared/types/inventory/location";
import type { LocationResponse } from "@/app/api/types/location";
import type { UUID } from "crypto";

/**
 * Convert an offline-first `Location` (camelCase) into the legacy
 * `LocationResponse` shape (snake_case) used by recap components.
 *
 * Pure function — exported for tests.
 */
export function mapLocationToResponse(loc: OfflineLocation): LocationResponse {
  return {
    id: loc.id as UUID,
    company_id: loc.companyId as UUID,
    name: loc.name,
    code: loc.code,
    city: loc.city,
    address: loc.address,
    is_active: loc.isActive,
    location_metadata: loc.locationMetadata,
    created_at: loc.createdAt,
    updated_at: loc.updatedAt,
  };
}

export interface UseRecapLocationsOfflineResult {
  data: LocationResponse[];
  isLoading: boolean;
  isError: boolean;
  error: null;
}

/**
 * Recap-only adapter that sources locations from the offline-first
 * `useLocations()` hook (Dexie + SSE) instead of polling the legacy
 * `/locations` endpoint.
 *
 * Filters to active locations only (matches the legacy
 * `useGlobalLocations({ is_active: true })` behavior) and emits the same
 * shape (`{ data, isLoading }`) so recap consumers don't change.
 */
export function useRecapLocationsOffline(options?: {
  enabled?: boolean;
}): UseRecapLocationsOfflineResult {
  const enabled = options?.enabled ?? true;
  const offlineLocations = useLocations();

  const data = useMemo<LocationResponse[]>(() => {
    if (!enabled) return [];
    return offlineLocations
      .filter((loc) => loc.isActive)
      .map(mapLocationToResponse);
  }, [offlineLocations, enabled]);

  return {
    data,
    isLoading: false,
    isError: false,
    error: null,
  };
}
